import * as React from "react";
import { cn } from "../utils";
import { DataTable, type DataTableProps } from "./data-table";
import { hangClass } from "./grid";

/**
 * The ONE tabular surface language (styleguide/redesign/SYSTEM.md §3.3–§3.4).
 *
 *   · `MetricStrip` — the metric caption attached to a ledger: ≤ 3 cells, even
 *     thirds, separated by whitespace (no vertical dividers). Label `type-subtext`
 *     muted sentence case, value `type-heading leading-display font-medium tabular-nums`
 *     (converged with StatCard's big number, PRD T2e), context a
 *     text delta/scope note (never a graph). With `onSelect` each cell is a
 *     nav-like scope selector over the ledger's rows (`aria-pressed` + a 2px
 *     primary underline); without it the strip is a static read.
 *   · `Ledger` — Card + optional strip + `DataTable`, one typography scale. A
 *     THIN wrapper: the strip rides DataTable's `summary` caption-band seam
 *     (flush above the header row, inside the one Card), `wrapInCard` is forced
 *     on, and every DataTable prop passes through. It HANGS by default so its
 *     first-column content (and the strip's first cell) lands on grid line 1.
 *
 * Table rules the Ledger expects (SYSTEM.md §4): no vertical column lines (the
 * table atoms are line-less), every column LEFT-aligned (no `align: "right"`),
 * fixed widths in twelfths (`w-N/12` + `tableClassName="table-fixed"`) with
 * `truncate` on wide columns, sortable/filterable only where it serves the
 * day's decision. The page owns filter state; the Ledger receives filtered rows.
 */

export interface MetricCell {
  key: string;
  /** Sentence case — no per-page uppercase/lowercase drift. */
  label: string;
  value: React.ReactNode;
  /** Quiet context line under the value (a text delta, a scope note). */
  context?: React.ReactNode;
}

export function MetricStrip({
  cells,
  selected,
  onSelect,
  className,
}: {
  cells: MetricCell[];
  /** The key of the active cell (aria-pressed + the 2px primary underline). */
  selected?: string;
  onSelect?: (key: string) => void;
  className?: string;
}) {
  return (
    <div className={cn("flex flex-col border-b border-border sm:flex-row", className)}>
      {cells.map((c) => {
        const isSelected = selected === c.key;
        // Even thirds, no vertical dividers — cells separate on whitespace only,
        // matching the line-less tables. A selected cell takes the 2px primary
        // underline (inset so layout never shifts). px-4 shares the table atoms'
        // cell inset so the first cell sits on the hung ledger's grid line.
        // Top-anchored flex column: a context-less cell must keep its label on
        // the shared label line beside 3-line cells (a bare <button> would
        // center its content vertically).
        const cellClass = cn(
          "flex min-w-0 flex-1 flex-col items-start justify-start px-4 py-3 text-left",
          "border-t border-border first:border-t-0 sm:border-t-0",
          isSelected && "shadow-[inset_0_-2px_0_0_var(--primary)]",
        );
        const body = (
          <>
            <div className="type-subtext text-muted-foreground">{c.label}</div>
            <div className="mt-1 type-heading leading-display font-medium tabular-nums text-foreground">{c.value}</div>
            {c.context != null && (
              <div className="mt-0.5 type-subtext text-muted-foreground">{c.context}</div>
            )}
          </>
        );
        return onSelect ? (
          // button-ignore: metric cell — a nav-like scope selector over the ledger's rows, not an action button.
          <button
            key={c.key}
            type="button"
            aria-pressed={isSelected}
            onClick={() => onSelect(c.key)}
            className={cn(cellClass, "cursor-pointer transition-colors hover:bg-secondary/40")}
          >
            {body}
          </button>
        ) : (
          <div key={c.key} className={cellClass}>
            {body}
          </div>
        );
      })}
    </div>
  );
}

export interface LedgerProps<R> extends Omit<DataTableProps<R>, "wrapInCard" | "summary"> {
  /** A `<MetricStrip>` sealed flush above the column-header row (one Card —
   *  rides DataTable's `summary` caption-band seam). */
  strip?: React.ReactNode;
  /**
   * The hang (SYSTEM.md §2.3): the card's border/header-band/row-hover bleed
   * OUTWARD into the gutter by exactly the first cell's inset (1px + 1rem), so
   * the ledger's content — first column AND the strip's first cell — lands on
   * the grid line, flush with the plain-text headings above it. Default
   * `"both"` (a full-width ledger). Use `"left"`/`"right"` for a ledger in a
   * split row (hang the OUTER edge only). `false` opts out.
   */
  hang?: "both" | "left" | "right" | false;
}

/** Card + optional metric strip + DataTable — the one tabular recipe. Hangs its
 *  chrome into the gutter so its content aligns to the grid. */
export function Ledger<R>({ strip, hang = "both", className, ...rest }: LedgerProps<R>) {
  return <DataTable {...rest} className={cn(hangClass(hang), className)} wrapInCard summary={strip} />;
}
